// WX JWT (token_employer) management utility
// Mirrors WX logic: store token by using environment-specific keys
// across localStorage and sessionStorage for unified auth enabled accounts.

import { type Whoami } from '@/types/whoami';

import { domainMeta } from './domain-meta';

const BASE_KEY = 'token_employer';

function stageSuffix(): string {
  return domainMeta.environment;
}

function getStageKey(): string {
  return `${BASE_KEY}_${stageSuffix()}`;
}

export function storeWxJwt(jwt: string): void {
  if (!jwt) return;
  const stageKey = getStageKey();
  try {
    localStorage.setItem(stageKey, jwt);
  } catch {
    // ignore storage errors (private mode, quota, etc.)
  }
  try {
    sessionStorage.setItem(stageKey, jwt);
  } catch {
    // ignore
  }
}

// JWT ready promise management
let jwtReadyPromise: Promise<boolean> | null = null;
let jwtReadyResolve: ((value: boolean) => void) | null = null;
let isFetching = false;

// Initialize the ready promise
function initJwtReadyPromise(): void {
  if (!jwtReadyPromise) {
    jwtReadyPromise = new Promise<boolean>(resolve => {
      jwtReadyResolve = resolve;
    });
  }
}

function deriveAccountSlugFromLocation(): string | null {
  try {
    const path = window.location.pathname.split('/').filter(Boolean);
    return path[0] ?? null;
  } catch {
    return null;
  }
}

function buildWxJwtUrl(whoami: Whoami): string | null {
  const slug =
    whoami?.account_slug ??
    whoami?.account_name ??
    deriveAccountSlugFromLocation();
  if (!slug) return null;
  return `${domainMeta.monolithOrigin}/${slug}/integrations/wx_jwt`;
}

export function fetchAndStoreWxJwt(whoami: Whoami): Promise<boolean> | null {
  // Initialize the ready promise if not already done
  initJwtReadyPromise();

  // If already fetching, return the existing promise
  if (isFetching) {
    return jwtReadyPromise;
  }

  // Set fetching flag
  isFetching = true;

  // Start the fetch asynchronously
  void (async () => {
    try {
      const url = buildWxJwtUrl(whoami);
      if (!url) {
        // No URL means we can't fetch JWT, resolve as ready (not needed)
        jwtReadyResolve?.(false);
        return;
      }

      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'X-Requested-With': 'XMLHttpRequest',
        },
        credentials: 'include',
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch WX JWT: ${response.status}`);
      }

      const data = (await response.json()) as { jwt?: string };
      if (data?.jwt) {
        storeWxJwt(data.jwt);
        // JWT successfully stored, resolve as ready
        jwtReadyResolve?.(true);
        return;
      }

      // No JWT in response, resolve as ready (not available)
      jwtReadyResolve?.(false);
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error('#ccd2d288 Failed to fetch and store WX JWT', e);
      // Error occurred, resolve as ready (failed)
      jwtReadyResolve?.(false);
    } finally {
      isFetching = false;
    }
  })();

  // Always return the ready promise
  return jwtReadyPromise;
}

export function getWxJwt(): string | null {
  const stageKey = getStageKey();
  try {
    return (
      sessionStorage.getItem(stageKey) ?? localStorage.getItem(stageKey) ?? null
    );
  } catch {
    return null;
  }
}
